/**
 * Zustand store configuration
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
  AppState,
  AuthState,
  ActivityLog,
  ActivityLogCategory,
  ThemeMode,
} from '../types';
import { THEME_MODES } from '../constants';
import { User } from 'core/services/user.service';
import { activityLogsService } from '../services/activity-logs.service';

// Auth Store
interface AuthStore extends AuthState {
  login: (user: User, token: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      set => ({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        login: (user: User, token: string) =>
          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          }),
        logout: () =>
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          }),
        setLoading: (loading: boolean) => set({ isLoading: loading }),
      }),
      {
        name: 'auth-storage',
        partialize: state => ({
          user: state.user,
          token: state.token,
          isAuthenticated: state.isAuthenticated,
        }),
      }
    )
  )
);

// Theme Store
interface ThemeStore extends ThemeMode {
  toggleTheme: () => void;
  setTheme: (mode: 'light' | 'dark') => void;
}

export const useThemeStore = create<ThemeStore>()(
  devtools(
    persist(
      (set, get) => ({
        mode: THEME_MODES.LIGHT,
        toggleTheme: () =>
          set({
            mode:
              get().mode === THEME_MODES.LIGHT
                ? THEME_MODES.DARK
                : THEME_MODES.LIGHT,
          }),
        setTheme: (mode: 'light' | 'dark') => set({ mode }),
      }),
      {
        name: 'theme-storage',
      }
    )
  )
);

// AuditLogs Store
interface ActivityLogsStore {
  items: ActivityLog[];
  categories: ActivityLogCategory[];
  selectedCategory: string | null;
  isLoading: boolean;
  error: string | null;
  stats: {
    total: number;
    byCategory: Record<string, number>;
    bySeverity: Record<string, number>;
    byStatus: Record<string, number>;
  } | null;
  setItems: (items: ActivityLog[]) => void;
  setCategories: (categories: ActivityLogCategory[]) => void;
  setSelectedCategory: (categoryId: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  addItem: (item: ActivityLog) => void;
  updateItem: (id: string, updates: Partial<ActivityLog>) => void;
  removeItem: (id: string) => void;
  loadActivityLogs: () => Promise<void>;
  loadRecentActivityLogs: (limit?: number) => Promise<void>;
  loadMyActivityLogs: (userId: string, limit?: number) => Promise<void>;
  loadActivityLogStats: () => Promise<void>;
}

export const useActivityLogsStore = create<ActivityLogsStore>()(
  devtools((set, get) => ({
    items: [],
    categories: [],
    selectedCategory: null,
    isLoading: false,
    error: null,
    stats: null,
    setItems: (items: ActivityLog[]) => set({ items }),
    setCategories: (categories: ActivityLogCategory[]) => set({ categories }),
    setSelectedCategory: (categoryId: string | null) =>
      set({ selectedCategory: categoryId }),
    setLoading: (loading: boolean) => set({ isLoading: loading }),
    setError: (error: string | null) => set({ error }),
    addItem: (item: ActivityLog) =>
      set(state => ({
        items: [...state.items, item],
      })),
    updateItem: (id: string, updates: Partial<ActivityLog>) =>
      set(state => ({
        items: state.items.map(item =>
          item.id === id ? { ...item, ...updates } : item
        ),
      })),
    removeItem: (id: string) =>
      set(state => ({
        items: state.items.filter(item => item.id !== id),
      })),
    loadActivityLogs: async () => {
      try {
        set({ isLoading: true, error: null });
        const response = await activityLogsService.getActivityLogs();
        const categories = await activityLogsService.getCategories();
        set({ items: response.data, categories, isLoading: false });
      } catch (error) {
        set({
          error:
            error instanceof Error ? error.message : 'Failed to load Auditlogs',
          isLoading: false,
        });
      }
    },
    loadRecentActivityLogs: async (limit: number = 5) => {
      try {
        set({ isLoading: true, error: null });
        const logs = await activityLogsService.getRecentActivityLogs(limit);
        set({ items: logs, isLoading: false });
      } catch (error) {
        set({
          error:
            error instanceof Error
              ? error.message
              : 'Failed to load recent Auditlogs',
          isLoading: false,
        });
      }
    },
    loadMyActivityLogs: async (userId: string, limit?: number) => {
      try {
        set({ isLoading: true, error: null });
        const logs = await activityLogsService.getMyActivityLogs(userId, limit);
        set({ items: logs, isLoading: false });
      } catch (error) {
        set({
          error:
            error instanceof Error
              ? error.message
              : 'Failed to load my Auditlogs',
          isLoading: false,
        });
      }
    },
    loadActivityLogStats: async () => {
      try {
        const stats = await activityLogsService.getActivityLogStats();
        set({ stats });
      } catch (error) {
        console.error('Failed to load Auditlog stats:', error);
      }
    },
  }))
);

// Combined store selector
export const useAppStore = () => ({
  auth: useAuthStore(),
  theme: useThemeStore(),
  activityLogs: useActivityLogsStore(),
});
